import React, {useMemo} from "react";
import {useFormik} from "formik";
import {InputProps} from "../Input/interface";
import {FormProps} from "./interface";
import {ButtonContainer, Form as StyledForm} from "./style";
import {Inputs} from "../Input/useInputElements";
import {Buttons} from "../Button/useButtonElements";
import {ButtonProps} from "../Button/interface";

const getInitialValues = (inputs?: InputProps[]) => {
  return (
    inputs?.reduce((acc: Record<string, any>, item) => {
      if (item.type === "checkbox") {
        acc[item.id] = item?.defaultValue ?? item?.defaultChecked ?? false;
      } else {
        acc[item.id] = item?.defaultValue ?? "";
      }
      return acc;
    }, {} as Record<string, any>) ?? {}
  );
};

const Form = (props: FormProps) => {
  const {inputs, buttons, onSubmit} = props;
  const {
    handleChange,
    handleSubmit,
    setFieldTouched,
    setFieldError,
    getFieldMeta,
    errors,
    values,
  } = useFormik({
    initialValues: getInitialValues(inputs),
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      onSubmit?.(values);
    },
  });
  const _inputs: InputProps[] = useMemo(() => {
    return (
      inputs?.map((input) => {
        const {value, touched, error} = getFieldMeta(input.id);
        return {
          ...input,
          onChange: handleChange,
          value: value,
          error: touched ? error : undefined,
          setError: (error: string | undefined) => {
            setFieldTouched(input.id, true, false);
            setFieldError(input.id, error);
          },
        };
      }) ?? []
    );
  }, [inputs, handleChange, setFieldError, setFieldTouched, getFieldMeta]);

  const allRequiredComplete = useMemo(() => {
    return inputs?.every((input) => {
      if (!input.required) {
        return true; // Non-required fields don't affect completion
      }
      
      const currentValue = (values as any)?.[input.id];
      
      // For checkboxes, false means incomplete if required
      if (input.type === "checkbox") {
        return currentValue === true;
      }
      
      // For radio buttons, empty string means no selection made
      if (input.type === "radio") {
        return currentValue !== "" && currentValue !== undefined;
      }
      
      // For other inputs, empty string or undefined means incomplete
      return currentValue !== "" && currentValue !== undefined;
    });
  }, [inputs, values]);

  const _buttons: ButtonProps[] = useMemo(() => {
    const isDisabled =
      Object.keys(errors).length > 0 || allRequiredComplete === false;
    const defaultButtonStyle: Partial<ButtonProps> = {
      style: {
        width: "100%",
      },
    };
    return (
      buttons?.map((button) => {
        if (button.type === "submit") {
          return {
            ...button,
            ...defaultButtonStyle,
            disabled: button.disabled !== undefined ? button.disabled : isDisabled,
          };
        }
        return {...button, ...defaultButtonStyle};
      }) ?? [
        {
          id: "todo-create-a-submit-button-for-form",
          label: "Complete",
          type: "submit",
          ...defaultButtonStyle,
          disabled: isDisabled,
        },
      ]
    );
  }, [buttons, errors, allRequiredComplete]);

  // Check if any submit button has explicit disabled state that differs from internal validation
  const hasExternalDisabledLogic = buttons?.some(button => 
    button.type === "submit" && button.disabled !== undefined
  );

  // Allow form submission if buttons have external disabled logic, even if internal validation fails
  const allowSubmission = hasExternalDisabledLogic || allRequiredComplete;

  return (
    <StyledForm onSubmit={allowSubmission ? handleSubmit : undefined}>
      <Inputs inputs={_inputs} />
      <ButtonContainer>
        <Buttons buttons={_buttons} />
      </ButtonContainer>
    </StyledForm>
  );
};

export default Form;
